import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AdminShell } from '../../components/AdminShell';
import { supabase } from '../../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const callJson = async (path, { tries = 3, method = 'GET', body } = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'Authorization': `Bearer ${session?.access_token}` };
  const init = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  let last;
  for (let i = 0; i < tries; i++) {
    try {
      const res = await fetch(`${BACKEND_URL}${path}`, init);
      if (res.ok) return res.json();
      last = new Error(`HTTP ${res.status}`);
      // Retry only on 5xx / 503; bail on client-side errors.
      if (res.status < 500) throw last;
    } catch (e) { last = e; }
    await new Promise(r => setTimeout(r, 250 * (i + 1)));
  }
  throw last;
};

const fmtMoney = (n) => n == null ? '—' : `£${Math.round(Number(n)).toLocaleString('en-GB')}`;
const fmtInt = (n) => n == null ? '—' : Number(n).toLocaleString('en-GB');

// ---------- Block 1 ----------
const NeedsAttention = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    callJson('/api/admin/dashboard/needs-attention').then(setRows).catch(e => setError(e.message));
  }, []);
  return (
    <div className="panel" data-testid="dash-needs-attention">
      <h3 className="admin-info-header">Needs attention</h3>
      {error && <div style={{ padding: 16, color: 'var(--red)', fontSize: 'var(--fs-sm)' }} data-testid="dash-attention-error">Failed to load: {error}</div>}
      {!error && rows == null && <div style={{ padding: 20, color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Loading…</div>}
      {!error && rows && rows.length === 0 && (
        <div style={{ padding: 24, color: 'var(--text-mute)', fontSize: 'var(--fs-base)', textAlign: 'center' }} data-testid="dash-attention-empty">
          Nothing needs attention right now.
        </div>
      )}
      {!error && rows && rows.length > 0 && (
        <div>
          {rows.map((r, i) => (
            <Link
              key={i}
              to={`/admin/shops/${r.shop_id}`}
              className="infrow"
              style={{ minHeight: 46, borderBottom: '1px solid var(--line2)', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
              data-testid={`dash-attention-row-${r.kind}-${r.shop_id}`}
            >
              <span className="k" style={{ minWidth: 130 }}>{r.shop_name}</span>
              <span className="vv" style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', textAlign: 'left', flex: 1 }}>{r.reason}</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--acc)' }}>Open →</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Block 2 ----------
const BusinessSummary = () => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    callJson('/api/admin/dashboard/business-summary').then(setD).catch(e => setError(e.message));
  }, []);
  const cell = (label, value, reason, testid) => (
    <div className="panel" style={{ padding: '16px 18px' }} data-testid={testid}>
      <div style={{ fontSize: 'var(--fs-xs)', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-mute-2)', fontWeight: 'var(--fw-semibold)' }}>{label}</div>
      <div style={{ fontSize: 'var(--fs-4xl)', fontWeight: 'var(--fw-bold)', marginTop: 6, fontFamily: 'var(--ff-num)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {reason && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginTop: 4 }}>{reason}</div>}
    </div>
  );
  if (error) return <div className="panel" style={{ padding: 16, color: 'var(--red)' }} data-testid="dash-summary-error">Business summary failed to load: {error}</div>;
  if (!d) return <div className="panel" style={{ padding: 20, color: 'var(--mute)' }}>Loading business summary…</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }} data-testid="dash-summary">
      {cell('MRR', fmtMoney(d.mrr), d.reasons?.mrr_delta || '—', 'dash-summary-mrr')}
      {cell('Paying shops', `${d.paying_shops} / ${d.total_shops}`, null, 'dash-summary-paying')}
      {cell('Net movement · this month', '—', d.reasons?.net_movement, 'dash-summary-netmovement')}
      {cell('Shops to breakeven', '—', d.reasons?.breakeven, 'dash-summary-breakeven')}
    </div>
  );
};

// ---------- Block 3 ----------
const PlatformHealth = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    callJson('/api/admin/dashboard/platform-health').then(setRows).catch(e => setError(e.message));
  }, []);
  const dot = (s) => (
    <span style={{
      display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
      background: s === 'green' ? '#10b981' : s === 'amber' ? '#f59e0b' : s === 'red' ? '#ef4444' : '#cbd5e1',
      marginRight: 8,
    }} />
  );
  return (
    <div className="panel" data-testid="dash-platform-health">
      <h3 className="admin-info-header">Platform health</h3>
      {error && <div style={{ padding: 16, color: 'var(--red)' }} data-testid="dash-health-error">Failed to load: {error}</div>}
      {!error && rows == null && <div style={{ padding: 20, color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Loading…</div>}
      {!error && rows && rows.map((r) => (
        <div key={r.key} className="infrow" style={{ minHeight: 46, borderBottom: '1px solid var(--line2)' }} data-testid={`dash-health-${r.key}`}>
          <span className="k">{dot(r.status)}{r.label}</span>
          <span className="vv" style={{ fontSize: 'var(--fs-sm)' }}>
            {r.value}
            {r.detail && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', fontWeight: 'var(--fw-regular)' }}>{r.detail}</div>}
          </span>
        </div>
      ))}
    </div>
  );
};

// ---------- Block 4 ----------
const useAnimatedNumber = (target, isMoney = false) => {
  const [display, setDisplay] = useState(target || 0);
  const prev = useRef(target || 0);
  useEffect(() => {
    if (target == null) return;
    const from = prev.current;
    const to = target;
    if (from === to) return;
    const duration = 600;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const v = from + (to - from) * p;
      setDisplay(v);
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return isMoney ? fmtMoney(display) : fmtInt(Math.round(display));
};

const PlatformTotals = () => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const load = () => callJson('/api/admin/dashboard/platform-totals').then(setD).catch(e => setError(e.message));
  useEffect(() => {
    load();
    let interval;
    const start = () => { interval = setInterval(load, 30000); };
    const stop = () => { if (interval) clearInterval(interval); interval = null; };
    if (!document.hidden) start();
    const onVis = () => { if (document.hidden) stop(); else { load(); start(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  const totalBk = useAnimatedNumber(d?.total_bookings);
  const deposits = useAnimatedNumber(d?.deposits_protected, true);
  const cell = (label, value, note, testid) => (
    <div className="panel" style={{ padding: '18px 20px' }} data-testid={testid}>
      <div style={{ fontSize: 'var(--fs-xs)', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-mute-2)', fontWeight: 'var(--fw-semibold)' }}>{label}</div>
      <div style={{ fontSize: 'var(--fs-4xl)', fontWeight: 'var(--fw-bold)', marginTop: 6, fontFamily: 'var(--ff-num)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {note && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginTop: 4 }}>{note}</div>}
    </div>
  );
  if (error) return <div className="panel" style={{ padding: 16, color: 'var(--red)' }} data-testid="dash-totals-error">Totals failed to load: {error}</div>;
  if (!d) return <div className="panel" style={{ padding: 20, color: 'var(--mute)' }}>Loading totals…</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }} data-testid="dash-totals">
      {cell('Total bookings', totalBk, null, 'dash-totals-bookings')}
      {cell('Deposits protected', deposits, null, 'dash-totals-deposits')}
      {cell('Bookings outside opening hours', d.outside_hours_monitored ? fmtInt(d.outside_hours) : '—',
        d.outside_hours_monitored ? null : 'Not yet monitored — needs a per-booking outside-hours flag.', 'dash-totals-outside')}
    </div>
  );
};

// ---------- Block 5 ----------
const ShopsBlock = () => {
  const [shops, setShops] = useState(null);
  const [extras, setExtras] = useState({});
  const [error, setError] = useState('');
  // Per-row disable while an sms_enabled toggle is in-flight, so a
  // double-click can't fire two conflicting updates against the
  // shops table.
  const [smsBusy, setSmsBusy] = useState({});

  const toggleSms = async (sh) => {
    const next = !sh.sms_enabled;
    setSmsBusy(b => ({ ...b, [sh.id]: true }));
    // Optimistic — the RLS guard on `shops` already restricts writes
    // to master_admin (see master-admin-bypass.sql) so a failure here
    // is a hard permission problem, not a race.
    setShops(prev => prev.map(s => s.id === sh.id ? { ...s, sms_enabled: next } : s));
    const { error: ue } = await supabase
      .from('shops').update({ sms_enabled: next }).eq('id', sh.id);
    if (ue) {
      // Revert.
      setShops(prev => prev.map(s => s.id === sh.id ? { ...s, sms_enabled: !next } : s));
      setError(`SMS toggle failed: ${ue.message}`);
    }
    setSmsBusy(b => ({ ...b, [sh.id]: false }));
  };
  useEffect(() => {
    Promise.all([
      supabase.from('shops').select('id,name,subdomain,plan,subscription_status,sms_enabled').order('name'),
      callJson('/api/admin/dashboard/shop-extras'),
    ]).then(([shopsRes, ex]) => {
      if (shopsRes.error) throw shopsRes.error;
      setShops(shopsRes.data || []);
      setExtras(ex || {});
    }).catch(e => setError(e.message || String(e)));
  }, []);
  return (
    <div className="panel" data-testid="dash-shops">
      <h3 className="admin-info-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        Shops
        <button
          type="button"
          className="btn s"
          style={{ marginLeft: 'auto', fontSize: 11, padding: '3px 10px' }}
          data-testid="dash-shops-reset-sms"
          onClick={async () => {
            if (!window.confirm('Reset ALL SMS counts across every shop? This deletes the entire sms_log table. Only use pre-launch.')) return;
            try {
              const r = await callJson('/api/admin/sms/reset', { method: 'POST' });
              alert(`Deleted ${r.deleted} SMS log rows.`);
              // Refresh derived counts.
              const ex = await callJson('/api/admin/dashboard/shop-extras');
              setExtras(ex || {});
            } catch (e) { alert('Reset failed: ' + (e.message || e)); }
          }}
          title="Delete all sms_log rows (zeros the counters). Pre-launch only."
        >Reset SMS counts</button>
      </h3>
      {error && <div style={{ padding: 16, color: 'var(--red)' }} data-testid="dash-shops-error">Failed to load: {error}</div>}
      {!error && shops == null && <div style={{ padding: 20, color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Loading…</div>}
      {!error && shops && shops.length === 0 && <div style={{ padding: 24, color: 'var(--text-mute)', fontSize: 'var(--fs-base)', textAlign: 'center' }}>No shops yet.</div>}
      {!error && shops && shops.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Shop</th>
              <th>Plan</th>
              <th>Billing</th>
              <th>SMS</th>
              <th>SMS used</th>
              <th>Cash share (30d)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {shops.map(sh => {
              const ex = extras[sh.id] || { sms_used: 0, sms_cap: 0, cash_share_pct: null };
              const smsPct = ex.sms_cap ? Math.round((ex.sms_used / ex.sms_cap) * 100) : null;
              const smsClass = smsPct == null ? 'gray' : smsPct >= 80 ? 'amber' : 'gray';
              const billingLabel = !sh.subscription_status || sh.subscription_status === 'cancelled'
                ? 'Not subscribed'
                : sh.subscription_status === 'active' || sh.subscription_status === 'trialing' ? 'Active'
                : sh.subscription_status === 'past_due' || sh.subscription_status === 'unpaid' ? 'Past due'
                : sh.subscription_status;
              return (
                <tr key={sh.id} className="clickable" onClick={() => window.location.assign(`/admin/shops/${sh.id}`)} data-testid={`dash-shop-row-${sh.id}`} style={{ minHeight: 46 }}>
                  <td><b>{sh.name}</b><small style={{ display: 'block', color: 'var(--mute)' }}>{sh.subdomain}.instacut.co.uk</small></td>
                  <td><span className={`pill ${sh.plan === 'premium' ? 'prem' : 'gray'}`} style={{ textTransform: 'capitalize' }}>{sh.plan}</span></td>
                  <td><span className="pill gray">{billingLabel}</span></td>
                  <td onClick={(e) => e.stopPropagation()} data-testid={`dash-shop-sms-enabled-${sh.id}`}>
                    <button
                      type="button"
                      className={`pill ${sh.sms_enabled ? 'green' : 'gray'}`}
                      onClick={() => toggleSms(sh)}
                      disabled={smsBusy[sh.id]}
                      style={{ cursor: smsBusy[sh.id] ? 'default' : 'pointer', border: 0 }}
                      title={sh.sms_enabled ? 'Click to disable SMS for this shop' : 'Click to enable SMS for this shop'}
                    >
                      {sh.sms_enabled ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td className="mono" data-testid={`dash-shop-sms-${sh.id}`}>
                    {smsPct == null ? '—' : <span className={`pill ${smsClass}`}>{ex.sms_used} / {ex.sms_cap}</span>}
                  </td>
                  <td className="mono" data-testid={`dash-shop-cash-${sh.id}`}>
                    {ex.cash_share_pct == null ? <span style={{ color: 'var(--mute)' }}>—</span> : `${ex.cash_share_pct}% cash · ${ex.cash_share_n} settled`}
                  </td>
                  <td className="tac"><Link to={`/admin/shops/${sh.id}`} className="link" onClick={(e) => e.stopPropagation()}>Open →</Link></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ---------- Page shell ----------
const DashboardInner = () => (
  <main className="main admin-page" data-testid="admin-dashboard-page">
    <div className="topbar">
      <div>
        <h2>Dashboard</h2>
        <div className="sub">Everything that needs a decision, at a glance.</div>
      </div>
    </div>
    <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <NeedsAttention />
      <BusinessSummary />
      <PlatformHealth />
      <PlatformTotals />
      <ShopsBlock />
    </div>
  </main>
);

const Dashboard = () => <AdminShell><DashboardInner /></AdminShell>;

export default Dashboard;
